import { useRouter } from 'next/router'
import { identity, propOr } from 'ramda'
import { useEffect, useMemo, useState } from 'react'

import Button, { ButtonVariant } from '@/components/button'
import Link from '@/components/link'
import useAuth from '@/modules/user/stores/use-auth'

import useProgramRegistrations from '../../../dashboard/events/stores/use-program-registrations'
import useProgramRegistrationOptions, {
  withProgramRegistrationOptionsStore,
} from '../../registration/stores/use-program-registration-options'
import { getActiveRegistrations } from '../../utils/active-registrations'
import useProgram from '../stores/use-program'
import RegisteredSessions from './registered-sessions'
// import Slider from './slider'

const programSelector = propOr({}, 'program')
const registrationsSelector = propOr([], 'registrations')
const registerSelector = propOr(identity, 'register')
const registrationOptionsSelector = propOr([], 'registrationOptions')

const Details = () => {
  const {
    query: { handle },
  } = useRouter()
  const [loading, setloading] = useState(true)
  const program = useProgram(programSelector)
  const currentUser = useAuth.use.current()
  const registrations = useProgramRegistrations(registrationsSelector)
  const register = useProgramRegistrationOptions(registerSelector)
  const registrationOptions = useProgramRegistrationOptions(
    registrationOptionsSelector,
  )

  const activeRegistrations = useMemo(
    () => getActiveRegistrations(program.id, registrations),
    [program.id, registrations],
  )
  useEffect(() => {
    const timeout = setTimeout(() => setloading(false), 4000)

    return () => clearTimeout(timeout)
  }, [loading, activeRegistrations])

  return (
    <div className='mx-auto mt-14'>
      <div className='flex flex-col-reverse'>
        <div>
          <h1 className='custom-heading mb-6 font-bold uppercase text-theme-blue-gray'>
            {program.title}
          </h1>

          <h2 id='information-heading' className='sr-only'>
            Product information
          </h2>
          {/* <p className='custom-font-30'>
            Version {program.version} (Updated{' '}
            <time dateTime={program.version}>{program.version}</time>)
          </p> */}
        </div>
      </div>

      <p className='custom-font'>{program.intro}</p>
      {/* <Slider /> */}
      <RegisteredSessions />
      {loading && !activeRegistrations.length && (
        <div className='flex items-center justify-center'>
          <div role='status'>
            <svg
              aria-hidden='true'
              className='mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600'
              viewBox='0 0 100 101'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                fill='currentColor'
              />
              <path
                d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                fill='currentFill'
              />
            </svg>
            <span className='sr-only'>Loading...</span>
          </div>
        </div>
      )}
      {!activeRegistrations?.length && !loading && (
        <div className='mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2'>
          {!program?.onDemand ? (
            <Link
              href={currentUser ? `/program/${handle}/register` : '/auth/login'}
              className='max-w-[fit-content] hover:no-underline'
            >
              <Button
                variant={ButtonVariant.Primary}
                bg='bg-[#F59175]'
                tailFill='#F59175'
                className='cm-bg !px-3 !py-2 !text-base text-white'
                tailClassName='right-[unset] left-0 scale-x-[-1] -translate-x-[50%] translate-y-[41%] h-2'
              >
                <span className='custom-font translate-y-0.5'>
                  REGISTER {program.price}
                </span>
              </Button>
            </Link>
          ) : (
            <div className='max-w-[fit-content] hover:no-underline'>
              <Button
                variant={ButtonVariant.Primary}
                bg='bg-[#F59175]'
                tailFill='#F59175'
                className='cm-bg !px-3 !py-2 !text-base text-white'
                tailClassName='right-[unset] left-0 scale-x-[-1] -translate-x-[50%] translate-y-[41%] h-2'
                onClick={() =>
                  register(program?.id, registrationOptions[0]?.sk?.slice(7))
                }
              >
                <span className='custom-font translate-y-0.5'>
                  REGISTER {program.price}
                </span>
              </Button>
            </div>
          )}
        </div>
      )}
      <style jsx>{`
        .custom-heading {
          font-size: calc(25px + (55 - 25) * (100vw - 375px) / (1920 - 375));
        }
        .custom-font {
          font-size: calc(16px + (30 - 16) * (100vw - 375px) / (1920 - 375));
          font-weight: 300;
        }
      `}</style>
    </div>
  )
}

export default withProgramRegistrationOptionsStore(Details)
